import { Root, RootContent } from "mdast";
import React, { Fragment, ReactNode } from "react";
import invariant from "tiny-invariant";
import { ItemLink, ItemLinkData } from "~/components/item/ItemLink";
import { LocLink, LocLinkData } from "~/components/locs/LocLink";
import { NpcLink, NpcLinkData } from "~/components/npc/NpcLink";
import { ProfLink, ProfLinkData } from "~/components/prof/ProfLink";
import { QuestLink, QuestLinkData } from "~/components/quest/QuestLink";
import { HarborBage } from "~/components/site/Badge";
import { Code } from "~/components/site/markdown/Code";
import { Table } from "~/components/site/markdown/Table";
import { SkillLink, SkillLinkData } from "~/components/skill/SkillLink";
import { GameVer } from "./Lang";
import { Image } from "~/components/site/markdown/Image";
import { Heading2, Heading3 } from "~/components/ui/Heading";
import { Blockquote } from "~/components/site/markdown/Blockquote";
import { Slug } from "./Slug";

export type MDDat = {
  items: Record<string, ItemLinkData | undefined>;
  npcs: Record<string, NpcLinkData | undefined | null>;
  quests: Record<string, QuestLinkData | undefined | null>;
  locs: Record<string, LocLinkData | undefined | null>;
  profs: Record<string, ProfLinkData | undefined | null>;
  skills: Record<string, SkillLinkData | undefined | null>;
};

export function renderMarkdown(md: { root: Root; dat: MDDat; gver: GameVer }) {
  return <>{renderChildren(md.root.children, md.dat, md.gver)}</>;
}

function renderContent(content: RootContent, dat: MDDat, gver: GameVer) {
  switch (content.type) {
    case "textDirective": {
      if (content.name === "item") {
        const enchant = parseInt(content.attributes?.["enchant"] ?? "0");
        const count = parseInt(content.attributes?.["count"] ?? "0");

        const first = content.children.at(0);
        invariant(first?.type === "text");

        const item = dat.items[first.value];

        if (item) {
          return <ItemLink item={item} count={count} enchant={enchant} />;
        }
      } else if (content.name === "npc") {
        const showLvl = "lvl" in content.attributes!;
        const showIcon = "icon" in content.attributes!;
        const first = content.children.at(0);
        invariant(first?.type === "text");
        const npc = dat.npcs[first.value];
        if (npc) {
          return <NpcLink npc={npc} showLvl={showLvl} showIcon={showIcon} />;
        }
      } else if (content.name === "quest") {
        const first = content.children.at(0);
        invariant(first?.type === "text");
        const quest = dat.quests[first.value];
        if (quest) {
          return <QuestLink quest={quest} />;
        }
      } else if (content.name === "loc") {
        const first = content.children.at(0);
        invariant(first?.type === "text");
        const loc = dat.locs[first.value];
        if (loc) {
          return <LocLink loc={loc} />;
        }
      } else if (content.name === "prof") {
        const first = content.children.at(0);
        invariant(first?.type === "text");
        const prof = dat.profs[first.value];
        if (prof) {
          return <ProfLink data={prof} />;
        }
      } else if (content.name === "skill") {
        const lvl = parseInt(content.attributes?.["lvl"] ?? "1");
        const first = content.children.at(0);
        invariant(first?.type === "text");
        const skill = dat.skills[`${first.value}|lvl=${lvl}`];
        if (skill) {
          return <SkillLink data={skill} />;
        }
      }
      return <span>LNK[{JSON.stringify(content)}]</span>;
    }

    case "paragraph": {
      return (
        <div style={{ margin: "10px 0" }}>
          {renderChildren(content.children, dat, gver)}
        </div>
      );
    }
    case "emphasis":
      return <em>{renderChildren(content.children, dat, gver)}</em>;
    case "blockquote":
      return (
        <Blockquote>{renderChildren(content.children, dat, gver)}</Blockquote>
      );
    case "break":
      return <br />;
    case "code":
      return <Code>{content.value}</Code>;
    case "definition":
      // TODO: What is this?
      return null;
    case "delete":
      return <del>{renderChildren(content.children, dat, gver)}</del>;
    case "footnoteDefinition":
    case "footnoteReference":
      // TODO
      return null;
    case "heading":
      const headingText =
        content.children.find((c) => c.type === "text")?.value || "section";
      const id = Slug(headingText);
      switch (content.depth) {
        case 1:
          return renderChildren(content.children, dat, gver);
        case 2:
          return (
            <Heading2 id={id}>
              {renderChildren(content.children, dat, gver)}
            </Heading2>
          );
        case 3:
          return (
            <Heading3 id={id}>
              {renderChildren(content.children, dat, gver)}
            </Heading3>
          );
        case 4:
          return renderChildren(content.children, dat, gver);
        case 5:
          return renderChildren(content.children, dat, gver);
        case 6:
          return renderChildren(content.children, dat, gver);
        default:
          content.depth satisfies never;
          return null;
      }
    case "html":
      return null;
    case "image":
      // if (!content.url.startsWith("/s/")) {
      //   return <span style={{ color: "red" }}>⚠ Invalid image address</span>;
      // }
      return null;

    case "imageReference":
      // TODO: What is this?
      return null;
    case "inlineCode":
      return <code style={{ display: "inline-block" }}>{content.value}</code>;
    case "link":
      // TODO
      return renderChildren(content.children, dat, gver);
    case "linkReference":
      // TODO: What is this?
      return null;
    case "list":
      const listContent = content.children.map((item, i) => {
        return (
          <li key={i}>
            {typeof item.checked === "boolean" ? (
              <input type="checkbox" checked={item.checked} />
            ) : null}
            {renderChildren(item.children, dat, gver)}
          </li>
        );
      });
      if (content.ordered) {
        return <ol>{listContent}</ol>;
      } else {
        return <ul>{listContent}</ul>;
      }
    case "listItem":
      // Should never happen. List items are handled inside of "list".
      return null;
    case "strong":
      return <strong>{renderChildren(content.children, dat, gver)}</strong>;
    case "table":
      return (
        <Table
          table={content}
          dat={dat}
          gver={gver}
          renderChildren={renderChildren}
        />
      );
    case "tableCell":
    case "tableRow":
      // Handled in "table".
      return null;
    case "thematicBreak":
    case "yaml":
      // TODO: What is this?
      return null;
    case "containerDirective":
      if (content.name === "gver") {
        const versions = content.attributes?.v?.split(",").map((v) => v.trim());
        return (
          <>
            {Array.isArray(versions) &&
              versions.includes(gver) &&
              renderChildren(content.children, dat, gver)}
          </>
        );
      }
      return null;
    case "leafDirective":
      if (content.name === "harbor") {
        return <HarborBage />;
      }

      const first = content.children.at(0);
      if (content.name === "img") {
        const alt = content.attributes?.["alt"];
        const width = content.attributes?.["width"];
        const height = content.attributes?.["height"];
        const border = content.attributes?.["border"];
        const float = content.attributes?.["float"] as
          | "left"
          | "right"
          | "center"
          | undefined;

        const first = content.children.at(0);

        let src = "";

        if (first?.type === "text") {
          src = first.value.trim();
        } else if (first?.type === "link") {
          src = first.url.trim();
        }

        if (!src) {
          return <span style={{ color: "red" }}>⚠ Invalid image address</span>;
        }

        const allowedDomain =
          "https://frp84uhvmi1z.compat.objectstorage.eu-frankfurt-1.oraclecloud.com/l2hub-pub/";

        if (!src.startsWith("/s/") && !src.startsWith(allowedDomain)) {
          return <span style={{ color: "red" }}>⚠ Invalid image address</span>;
        }

        return (
          <Image
            src={src}
            alt={alt ?? undefined}
            width={width ? parseInt(width) : undefined}
            height={height ? parseInt(height) : undefined}
            float={float}
            border={border ? parseInt(border) : undefined}
          />
        );
      }

      return null;
    case "text": {
      return content.value;
    }
    default:
      content satisfies never;
      return <code>{JSON.stringify(content, undefined, 2)}</code>;
  }
}

function renderChildren(
  children: RootContent[],
  dat: MDDat,
  gver: GameVer
): ReactNode {
  return React.createElement(
    Fragment,
    null,
    ...children.map((child) => renderContent(child, dat, gver))
  );
}

export function collectHeadings(
  root: Root
): { depth: number; text: string; id: string }[] {
  const headings: { depth: number; text: string; id: string }[] = [];

  function walk(nodes: RootContent[]) {
    for (const node of nodes) {
      if (node.type === "heading") {
        const textNode = node.children.find((c) => c.type === "text");
        if (textNode?.value) {
          const id = Slug(textNode.value);
          headings.push({
            depth: node.depth,
            text: textNode.value,
            id,
          });
        }
      }

      if ("children" in node && Array.isArray(node.children)) {
        walk(node.children);
      }
    }
  }

  walk(root.children);
  return headings;
}
